import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BaseService } from '@aifs-shared/common/base-service';
import { BaseResponse, ResponseResultCode } from '@aifs-shared/common/base-response';
import { Observable, Subject } from 'rxjs';

@Injectable()
export class CAutoService extends BaseService {

    public constructor(
        private http: HttpClient
    ) {
        super();
    }

    private handleRequest(request: Observable<BaseResponse>): Observable<BaseResponse> {
        const subject = new Subject<BaseResponse>();
        request.subscribe({
            next: (response: BaseResponse) => {
                if (response.resultCode === ResponseResultCode.Success) {
                    subject.next(response);
                }
            },
            error: (error) => {
                subject.error(error);
            }
        });
        return subject;
    }

    getCAutoAbeonaUsers(): Observable<CAutoUsersResponse> {
        const subject = new Subject<CAutoUsersResponse>();
        this.http.get<BaseResponse>(environment.ServiceUrl_CAuto_Users)
        .subscribe({
            next: (response: BaseResponse) => {
                if (response.resultCode === ResponseResultCode.Success) {
                    subject.next(this.getResultData<CAutoUsersResponse>(response));
                }
            },
            error: (error) => {
                subject.error(error)
            }
        });
        return subject;
    }

    getCAutoApplications(): Observable<CAutoApplicationsResponse> {
        const subject = new Subject<CAutoApplicationsResponse>();
        this.http.get<BaseResponse>(environment.ServiceUrl_CAuto_Applications)
        .subscribe({
            next: (response: BaseResponse) => {
                if (response.resultCode === ResponseResultCode.Success) {
                    subject.next(this.getResultData<CAutoApplicationsResponse>(response));
                }
            },
            error: (error) => {
                subject.error(error)
            }
        });
        return subject;
    }

    assignCAutoAbeonaUser(currentUserId: number, userIdToBeAssigned: number): Observable<BaseResponse> {
        return this.handleRequest(this.http.post<BaseResponse>(environment.ServiceUrl_CAuto_User_Reassign, { currentUserId, userIdToBeAssigned }));
    }

    placeApplicantAtTestCamp(applicantUserId: number): Observable<BaseResponse> {
        return this.handleRequest(this.http.post<BaseResponse>(environment.ServiceUrl_CAuto_Applications_PlaceAtTestCamp, { applicantUserId }));
    }

    createTestOrcaVisaDetails(applicantUserId: number): Observable<BaseResponse> {
        return this.handleRequest(this.http.post<BaseResponse>(environment.ServiceUrl_CAuto_Applications_CreateTestOrcaVisaDetails, { applicantUserId }));
    }

    createTestFlightDetails(applicantUserId: number): Observable<BaseResponse> {
        return this.handleRequest(this.http.post<BaseResponse>(environment.ServiceUrl_CAuto_Applications_CreateTestFlightDetails, { applicantUserId }));
    }

    resetAbeonaUser(name: string): Observable<BaseResponse>{
        const subject = new Subject<BaseResponse>()
        this.archiveUser(`cauto.${name}@aifs.co.uk`, TestUserDataPreset.PresetDict[name].role).subscribe({
            next: (response: BaseResponse)=> {
                if (response.resultCode === ResponseResultCode.Success) {
                    this.createUser(TestUserDataPreset.PresetDict[name]).subscribe({
                        next: (response: BaseResponse) => {
                            if (response.resultCode === ResponseResultCode.Success) {
                                subject.next(response);
                            }
                        },
                        error: (error) => {
                            subject.error(error)
                        }
                    })
                }
            },
            error: (error) => {
                subject.error(error)
            }
        });
        return subject;
    }

    private archiveUser(email: string, role: string): Observable<BaseResponse> {
        return this.handleRequest(this.http.post<BaseResponse>(environment.ServiceUrl_CAuto_Applications_ArchiveUser, { email: email, role: role }));
    }

    private confirmInterview(email: string): Observable<BaseResponse> {
        return this.handleRequest(this.http.post<BaseResponse>(`${CAutoService.API_URL}/user/cautomation/confirm-interview`, { email: email }));
    }

    private completeInterview(email: string): Observable<BaseResponse> {
        return this.handleRequest(this.http.post<BaseResponse>(`${CAutoService.API_URL}/user/cautomation/complete-interview`, { email: email }));
    }

    private createUser(user: TestUserData): Observable<BaseResponse> {
        const subject = new Subject<BaseResponse>()
        let body = {
            permissions: user.permissionIds,
            role: user.role,
            firstName: `CAuto ${user.countryCode} ${user.role}`,
            lastName: `Auto.${user.name}`,
            countryCode: user.countryCode,
            agencyDetails: user.role == 'AGT' ? {
                agencyId: user.agencyId,
                active: true
            } : null,
            staffDetails: user.role == 'STA' ? {
                "countryCodes": [],
                "offices": [
                    1,
                    2,
                    3,
                    4
                ],
                "seasonal": false,
                "active": true
            } : null,
            signinDetails: {
                existingAzureEmail: `cauto.${user.name}@aifs.co.uk`,
                newAzureEmail: false,
                userExists: false,
                linkedEmail: `cauto.${user.name}@aifs.co.uk`,
                telephone: "+48123123123"
            }
        }
        this.http.post<BaseResponse>(environment.ServiceUrl_CAuto_User_Create, body)
        .subscribe({
            next: (response: BaseResponse) => {
                if (response.resultCode === ResponseResultCode.Success) {
                    subject.next(response);
                }
            },
            error: (error) => {
                subject.error(error)
            }
        });

        return subject;
    }

    static CA18_URL = "https://campfire.mycampamerica.com";
    static API_URL = "https://campfire-api.mycampamerica.com"
}

export interface TestUserData {
    name: string;
    role: string;
    countryCode: string;
    agencyId: number;
    permissionIds: number[];
}

export interface CAutoAbeonaUser {
    userId: number;
    fullName: string;
    azureEmail: string;
    isInterviewer: boolean;
    isAgent: boolean;
    isStaff: boolean;
    officeOrAgency: string;
    isActive: boolean;
    isSeasonal: boolean;
    isTestUser: boolean;
}

class TestUserDataPreset {
    private static AllPermissions: number[] = [15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 41, 42, 43, 44, 45, 46, 47, 48, 49];
    static readonly Jim: TestUserData = {
        name: 'Jim',
        role: 'AGT',
        countryCode: 'GB',
        agencyId: 1,
        permissionIds: TestUserDataPreset.AllPermissions
    }
    static readonly Ewa: TestUserData = {
        name: 'Ewa',
        role: 'AGT',
        countryCode: 'PL',
        agencyId: 16,
        permissionIds: TestUserDataPreset.AllPermissions
    }
    static readonly Izzy: TestUserData = {
        name: 'Izzy',
        role: 'INT',
        countryCode: 'GB',
        agencyId: -1,
        permissionIds: TestUserDataPreset.AllPermissions
    }
    static readonly Pamela: TestUserData = {
        name: 'Pamela',
        role: 'STA',
        countryCode: 'GB',
        agencyId: 1,
        permissionIds: TestUserDataPreset.AllPermissions
    }

    static PresetDict:{ [key: string]: TestUserData } = {
        "Jim": this.Jim,
        "Ewa": this.Ewa,
        "Izzy": this.Izzy,
        "Pamela": this.Pamela
      };
}

export interface CAutoApplication {
    userId: number;
    email: string;
    fullName: string;
    status: string;
    isTestUser: boolean;
}

export class CAutoUsersResponse {
    users: CAutoAbeonaUser[] = [];
}

export class CAutoApplicationsResponse {
    applications: CAutoApplication[] = [];
}